import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';

interface ChurnAnalysisProps {
  data: any[];
}

export function ChurnAnalysis({ data }: ChurnAnalysisProps) {
  // Calcular retenção ao longo do tempo
  const retentionData = data.map((item, index) => {
    const firstUserCount = data[0].usuarios;
    const retentionRate = firstUserCount > 0 ? (item.usuarios / firstUserCount) * 100 : 0;

    return {
      date: item.date,
      retencao: parseFloat(retentionRate.toFixed(1)),
      usuarios: item.usuarios,
    };
  });

  const avgRetention =
    retentionData.reduce((sum, item) => sum + item.retencao, 0) / retentionData.length;
  const currentRetention = retentionData[retentionData.length - 1]?.retencao || 0;

  return (
    <div
      className="rounded-2xl p-6 backdrop-blur-sm border"
      style={{
        backgroundColor: '#111116',
        borderColor: '#1e1e25',
      }}
    >
      <div className="mb-6">
        <h3 style={{ color: '#f5f5f7' }} className="text-lg font-semibold">
          Retenção de Usuários
        </h3>
        <p style={{ color: '#8a8a95' }} className="text-sm">
          Taxa de retenção de usuários ao longo do tempo
        </p>
      </div>
      <div className="space-y-6">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={retentionData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="colorRetencao" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#b4a3ff" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#b4a3ff" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(138, 138, 149, 0.1)" />
            <XAxis
              dataKey="date"
              stroke="#75757f"
              style={{ fontSize: '12px' }}
            />
            <YAxis
              stroke="#75757f"
              style={{ fontSize: '12px' }}
              label={{ value: '%', angle: -90, position: 'insideLeft' }}
              domain={[0, 100]}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#131318',
                border: '1px solid #1e1e25',
                borderRadius: '8px',
                color: '#f5f5f7',
              }}
              formatter={(value: number) => `${value.toFixed(1)}%`}
            />
            <Legend wrapperStyle={{ color: '#75757f' }} />
            <Area
              type="monotone"
              dataKey="retencao"
              stroke="#b4a3ff"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorRetencao)"
              name="Taxa de Retenção"
              isAnimationActive={true}
            />
          </AreaChart>
        </ResponsiveContainer>

        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-6"
          style={{ borderColor: '#1e1e25' }}
        >
          <div
            className="p-4 rounded-lg border"
            style={{
              backgroundColor: 'rgba(180, 163, 255, 0.05)',
              borderColor: '#1e1e25',
            }}
          >
            <p style={{ color: '#8a8a95' }} className="text-sm mb-1">
              Retenção Atual
            </p>
            <p style={{ color: '#b4a3ff' }} className="text-2xl font-bold">
              {currentRetention.toFixed(1)}%
            </p>
            <p style={{ color: '#75757f' }} className="text-xs mt-1">
              Dia 30
            </p>
          </div>

          <div
            className="p-4 rounded-lg border"
            style={{
              backgroundColor: 'rgba(180, 163, 255, 0.05)',
              borderColor: '#1e1e25',
            }}
          >
            <p style={{ color: '#8a8a95' }} className="text-sm mb-1">
              Média
            </p>
            <p style={{ color: '#b4a3ff' }} className="text-2xl font-bold">
              {avgRetention.toFixed(1)}%
            </p>
            <p style={{ color: '#75757f' }} className="text-xs mt-1">
              Últimos 30 dias
            </p>
          </div>

          <div
            className="p-4 rounded-lg border"
            style={{
              backgroundColor: 'rgba(180, 163, 255, 0.05)',
              borderColor: '#1e1e25',
            }}
          >
            <p style={{ color: '#8a8a95' }} className="text-sm mb-1">
              Tendência
            </p>
            <p style={{ color: '#b4a3ff' }} className="text-2xl font-bold">
              {currentRetention >= avgRetention ? '📈' : '📉'}
            </p>
            <p style={{ color: '#75757f' }} className="text-xs mt-1">
              {currentRetention >= avgRetention ? 'Melhora' : 'Piora'}
            </p>
          </div>
        </div>

        <div
          className="p-4 rounded-lg border"
          style={{
            backgroundColor: 'rgba(138, 138, 149, 0.05)',
            borderColor: '#1e1e25',
          }}
        >
          <p style={{ color: '#8a8a95' }} className="text-sm">
            <span className="font-semibold">💡 Insight:</span> A taxa de retenção está
            {currentRetention >= avgRetention ? ' acima' : ' abaixo'} da média. Investir em
            recursos de engajamento pode melhorar a retenção de longo prazo.
          </p>
        </div>
      </div>
    </div>
  );
}
